import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Check, Minus } from 'lucide-react';
import { get } from '../../lib/apiClient';
import { cn } from '@vd/ui';
import { AdminTable, type AdminTableColumn, useFocusParam } from './AdminTable';
import { Sparkline } from './Sparkline';
import styles from './Admin.module.css';
import { formatDay } from '../../lib/time';

/**
 * Admin → Users.
 *
 * Reuses the shared AdminTable for sort + search; per-row affordances
 * (account chips → /admin/accounts?focus=<id>, MCP client label,
 * 30-day ticket-created sparkline) are this page's specific cell
 * renderers. Server returns the funnel booleans + mcpClient + the
 * sparkline data already shaped — no per-row queries here.
 */
interface AdminUserCompany {
  id: string;
  name: string;
  role: string;
}

interface AdminUserRow {
  id: string;
  email: string;
  name: string;
  isStaff: boolean;
  createdAt: string;
  lastActiveAt: string | null;
  companyCount: number;
  companies: AdminUserCompany[];
  mcpClient: string | null;
  mcpLastUsedAt: string | null;
  dailyCreated30: number[];
  funnel: {
    completedOnboarding: boolean;
    firstCompany: boolean;
    firstProject: boolean;
    firstExtensionToken: boolean;
    firstMcpToken: boolean;
    firstTicket: boolean;
    hasTeam: boolean;
  };
}

interface AdminUsersResponse {
  users: AdminUserRow[];
  total: number;
  limit: number;
  offset: number;
}

const FUNNEL_COLS: ReadonlyArray<{ key: keyof AdminUserRow['funnel']; label: string; title: string }> = [
  { key: 'completedOnboarding', label: 'Onboard', title: 'Completed onboarding questionnaire' },
  { key: 'firstProject', label: 'Project', title: 'Created at least one project' },
  { key: 'firstExtensionToken', label: 'Ext', title: 'Installed the Chrome extension' },
  { key: 'firstMcpToken', label: 'MCP', title: 'Set up the MCP connector' },
  { key: 'firstTicket', label: 'Ticket', title: 'Captured at least one ticket' },
  { key: 'hasTeam', label: 'Team', title: 'Belongs to a company with more than one member' },
];

export function UsersPage() {
  const focus = useFocusParam();
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => get<AdminUsersResponse>('/admin/users'),
    staleTime: 30_000,
  });

  const columns: AdminTableColumn<AdminUserRow>[] = [
    {
      key: 'user',
      label: 'User',
      sortable: true,
      sortAccessor: (r) => r.name.toLowerCase(),
      render: (u) => (
        <div className={styles.userCell}>
          <span className={styles.userName}>
            {u.name}
            {u.isStaff ? <span className={styles.staffBadge}>STAFF</span> : null}
          </span>
          <span className={styles.userEmail}>{u.email}</span>
        </div>
      ),
    },
    {
      key: 'created',
      label: 'Signed up',
      sortable: true,
      sortAccessor: (r) => r.createdAt,
      render: (u) => <span className={styles.muted}>{formatDate(u.createdAt)}</span>,
    },
    {
      key: 'lastActive',
      label: 'Last active',
      sortable: true,
      sortAccessor: (r) => r.lastActiveAt ?? '',
      render: (u) => <span className={styles.muted}>{formatDate(u.lastActiveAt)}</span>,
    },
    {
      key: 'accounts',
      label: 'Accounts',
      render: (u) => (
        <div className={styles.companyChips}>
          {u.companies.length === 0 ? (
            <span className={styles.muted}>-</span>
          ) : (
            u.companies.map((c) => (
              <button
                key={c.id}
                type="button"
                className={cn(styles.companyChip, c.role === 'owner' && styles.companyChipOwner, styles.linkChip)}
                onClick={(e) => { e.stopPropagation(); navigate(`/admin/accounts?focus=${encodeURIComponent(c.id)}`); }}
                title={`${c.name} · ${c.role} · click to open in Accounts`}
              >
                {c.name}
              </button>
            ))
          )}
        </div>
      ),
    },
    {
      key: 'mcp',
      label: 'MCP client',
      sortable: true,
      sortAccessor: (r) => r.mcpClient ?? '',
      render: (u) => u.mcpClient
        ? <span title={u.mcpLastUsedAt ? `Last used ${formatDate(u.mcpLastUsedAt)}` : undefined}>{u.mcpClient}</span>
        : <span className={styles.muted}>-</span>,
    },
    {
      key: 'sparkline',
      label: '30d tickets',
      title: 'Tickets created in the last 30 days (sparkline)',
      sortable: true,
      sortAccessor: (r) => r.dailyCreated30.reduce((a, b) => a + b, 0),
      render: (u) => {
        const total = u.dailyCreated30.reduce((a, b) => a + b, 0);
        if (total === 0) return <span className={styles.muted}>-</span>;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Sparkline values={u.dailyCreated30} label="tickets created" />
            <span style={{ fontSize: 11, color: 'var(--text-tertiary)', minWidth: 16 }}>{total}</span>
          </div>
        );
      },
    },
    ...FUNNEL_COLS.map<AdminTableColumn<AdminUserRow>>((c) => ({
      key: c.key,
      label: c.label,
      title: c.title,
      center: true,
      sortable: true,
      sortAccessor: (r) => r.funnel[c.key] ? 1 : 0,
      render: (u) => (u.funnel[c.key]
        ? <Check size={14} style={{ color: 'var(--success, #16a34a)' }} />
        : <Minus size={14} className={styles.muted} />),
    })),
  ];

  return (
    <>
      <h1 className={styles.h1}>Users</h1>
      <p className={styles.sub}>
        Signups + onboarding funnel + MCP client + 30-day activity. Click any
        account chip to jump to that account's view.
      </p>

      {error ? (
        <p style={{ color: 'var(--danger)' }}>
          Failed to load users: {error instanceof Error ? error.message : 'unknown error'}
        </p>
      ) : isLoading || !data ? (
        <p className={styles.muted}>Loading…</p>
      ) : (
        <AdminTable<AdminUserRow>
          columns={columns}
          rows={data.users}
          getRowId={(u) => u.id}
          searchTextFor={(u) => `${u.name} ${u.email} ${u.mcpClient ?? ''} ${u.companies.map((c) => c.name).join(' ')}`}
          searchPlaceholder="Search by name, email, account, or MCP client…"
          initialSortKey="created"
          initialSortDir="desc"
          highlightRowId={focus}
          emptyText="No users yet."
        />
      )}
    </>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    return formatDay(d);
  } catch {
    return '-';
  }
}
